import React, { useState, useRef, useEffect } from 'react';
import { useNotesStore } from '../store';
import api from '../api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Send, Loader2, Sparkles, BookOpen, Tags, MessageSquare } from 'lucide-react';

export default function AiPanel() {
  const { currentNote } = useNotesStore();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('chat');
  const [researchTopic, setResearchTopic] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const { response } = await api.aiChat(input, currentNote?.id);
      setMessages(prev => [...prev, { role: 'assistant', content: response }]);
    } catch (error) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Error: ' + error.message }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResearch = async (e) => {
    e.preventDefault();
    if (!researchTopic.trim() || isLoading) return;

    setIsLoading(true);
    try {
      const result = await api.createFromResearch(researchTopic, currentNote?.folder || '/Research');
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Created note: "${result.note.title}"\n\nTags: ${result.research.tags.join(', ')}`
      }]);
      setResearchTopic('');
    } catch (error) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Error: ' + error.message }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestMetadata = async () => {
    if (!currentNote) return;
    setIsLoading(true);
    try {
      const suggestions = await api.suggestMetadata(currentNote.title, currentNote.content);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Suggested tags: ${suggestions.tags.map(t => `#${t}`).join(', ')}\n\nSuggested links: ${suggestions.linkedNotes.map(l => `[[${l}]]`).join(', ')}`
      }]);
    } catch (error) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Error: ' + error.message }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-card border-l">
      {/* Tabs */}
      <div className="border-b">
        <div className="flex">
          {[
            { value: 'chat', icon: MessageSquare, label: 'Chat' },
            { value: 'research', icon: BookOpen, label: 'Research' },
            { value: 'organize', icon: Tags, label: 'Organize' }
          ].map(tab => (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-medium transition-colors ${
                activeTab === tab.value
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <tab.icon size={14} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {activeTab === 'chat' && (
          <>
            {messages.length === 0 && (
              <div className="text-center text-muted-foreground py-8">
                <Sparkles size={32} className="mx-auto mb-2 text-primary" />
                <p className="text-sm">Ask AI about your notes</p>
                {currentNote && (
                  <p className="text-xs mt-1 text-muted-foreground/70">
                    Context: {currentNote.title}
                  </p>
                )}
              </div>
            )}
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`p-3 rounded-lg text-sm ${
                  msg.role === 'user'
                    ? 'bg-primary text-primary-foreground ml-8'
                    : 'bg-muted mr-8'
                }`}
              >
                <div className="whitespace-pre-wrap">{msg.content}</div>
              </div>
            ))}
            {isLoading && (
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <Loader2 size={14} className="animate-spin" />
                Thinking...
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}

        {activeTab === 'research' && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Research a topic and create a comprehensive note
            </p>
            <form onSubmit={handleResearch} className="space-y-3">
              <Input
                type="text"
                value={researchTopic}
                onChange={(e) => setResearchTopic(e.target.value)}
                placeholder="Enter topic to research..."
              />
              <Button type="submit" className="w-full" disabled={isLoading || !researchTopic.trim()}>
                {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <BookOpen className="mr-2 h-4 w-4" />}
                Research & Create Note
              </Button>
            </form>

            {currentNote && (
              <div className="pt-4 border-t">
                <p className="text-sm text-muted-foreground mb-2">Expand existing note</p>
                <Button
                  variant="outline"
                  className="w-full"
                  disabled={isLoading}
                  onClick={async () => {
                    setIsLoading(true);
                    try {
                      await api.createFromResearch(currentNote.title, currentNote.folder, currentNote.id);
                      setMessages(prev => [...prev, { role: 'assistant', content: `Expanded "${currentNote.title}"` }]);
                    } catch (error) {
                      setMessages(prev => [...prev, { role: 'assistant', content: 'Error: ' + error.message }]);
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                >
                  <Sparkles className="mr-2 h-4 w-4" />
                  Expand This Note
                </Button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'organize' && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">AI-powered organization</p>

            {currentNote && (
              <Button variant="outline" className="w-full justify-start" disabled={isLoading} onClick={handleSuggestMetadata}>
                <Tags className="mr-2 h-4 w-4" />
                Suggest Tags & Links
              </Button>
            )}

            <Button
              variant="outline"
              className="w-full justify-start"
              disabled={isLoading}
              onClick={async () => {
                setIsLoading(true);
                try {
                  const result = await api.organizeNotes();
                  setMessages(prev => [...prev, { role: 'assistant', content: `Organized ${result.applied} notes` }]);
                } catch (error) {
                  setMessages(prev => [...prev, { role: 'assistant', content: 'Error: ' + error.message }]);
                } finally {
                  setIsLoading(false);
                }
              }}
            >
              <Sparkles className="mr-2 h-4 w-4" />
              Auto-Organize All Notes
            </Button>
          </div>
        )}
      </div>

      {/* Chat input */}
      {activeTab === 'chat' && (
        <form onSubmit={handleSendMessage} className="p-3 border-t">
          <div className="flex gap-2">
            <Input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask something..."
              disabled={isLoading}
            />
            <Button type="submit" size="icon" disabled={isLoading || !input.trim()}>
              <Send size={16} />
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
